import React from 'react';
import './CompactBudgetCard.css';

interface FiscalData {
  value: number;
  fiscal_year: number;
  fiscal_type: string;
}

interface CostItem {
  cost_item_name: string;
  standard_name?: string;
  spending_category?: string;
  description?: string;
  scost_item_supplier?: string;
  fiscal_data: FiscalData[];
}

interface Project {
  pjid: number;
  project_name: string;
  project_type?: string;
  project_description?: string;
  cost_items: CostItem[];
}

interface SearchResult {
  id: string;
  document_id?: number;
  peid?: number;
  line_item?: string;
  line_item_title: string;
  line_item_description?: string;
  organization: string;
  account?: string;
  account_title?: string;
  budget_activity_title?: string;
  program?: string;
  domain?: string;
  supplier?: string;
  projects: Project[];
  total_budget?: number;
  fiscal_years: number[];
  relevance_score?: number;
  highlight?: Record<string, string[]>;
  title: string;
  description: string;
  agency: string;
  budget?: number;
  category: string;
  status: string;
}

interface CompactBudgetCardProps {
  result: SearchResult;
  onSelect?: (id: string, selected: boolean) => void;
  onClick?: () => void;
  selected?: boolean;
  darkMode?: boolean;
  onBookmark?: (peid: number, title: string) => void;
  isBookmarked?: boolean;
}

const CompactBudgetCard: React.FC<CompactBudgetCardProps> = ({ result, onSelect, onClick, selected = false, darkMode = false, onBookmark, isBookmarked = false }) => {
  const formatCurrency = (amount?: number) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: amount >= 1000000 ? 'compact' : 'standard'
    }).format(amount);
  };


  const renderHighlightedText = (text: string, fieldName: string, maxLength: number = 100): React.ReactNode => {
    // Check if we have highlights for this field
    const highlights = result.highlight?.[fieldName];
    const textColor = darkMode ? '#f7fafc' : '#000000';
    
    if (!highlights || highlights.length === 0) {
      // No highlights, just truncate if needed and return with appropriate color
      const truncated = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
      return <span style={{color: textColor}}>{truncated}</span>;
    }
    
    // Use the first highlight (which should contain the matched text with <em> tags)
    const highlightedText = highlights[0];
    
    // Parse the highlighted text to create React elements
    const parts = highlightedText.split(/(<em>.*?<\/em>)/g);
    
    const elements = parts.map((part, index) => {
      if (part.startsWith('<em>') && part.endsWith('</em>')) {
        // This is a highlighted part
        const content = part.replace(/<\/?em>/g, '');
        return <mark key={index} className="search-highlight">{content}</mark>;
      }
      return <span key={index} style={{color: textColor}}>{part}</span>;
    });
    
    // If the result is too long, truncate it
    const textLength = highlightedText.replace(/<\/?em>/g, '').length;
    if (textLength > maxLength) {
      // For simplicity, just show the truncated version with appropriate color
      const truncated = text.substring(0, maxLength) + '...';
      return <span style={{color: textColor}}>{truncated}</span>;
    }
    
    return <span>{elements}</span>;
  };

  const getProjectCount = () => {
    return result.projects.length;
  };

  const getCostItemCount = () => {
    return result.projects.reduce((sum, project) => sum + project.cost_items.length, 0);
  };

  // Get highlight previews for nested fields
  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onBookmark && result.peid) {
      onBookmark(result.peid, result.line_item_title);
    }
  };

  const getNestedHighlights = () => {
    const highlights: Array<{type: 'project' | 'cost_item', text: string, name: string}> = [];
    
    // Check for project highlights
    const projectNameHighlights = result.highlight?.['projects.project_name'];
    const projectDescHighlights = result.highlight?.['projects.project_description'];
    
    if (projectNameHighlights?.length) {
      const cleanText = projectNameHighlights[0].replace(/<\/?em>/g, '');
      highlights.push({type: 'project', text: cleanText, name: 'Project'});
    } else if (projectDescHighlights?.length) {
      const cleanText = projectDescHighlights[0].replace(/<\/?em>/g, '');
      highlights.push({type: 'project', text: cleanText.substring(0, 50) + '...', name: 'Project'});
    }
    
    // Check for cost item highlights
    const costItemNameHighlights = result.highlight?.['projects.cost_items.cost_item_name'];
    const costItemDescHighlights = result.highlight?.['projects.cost_items.description'];
    
    if (costItemNameHighlights?.length) {
      const cleanText = costItemNameHighlights[0].replace(/<\/?em>/g, '');
      highlights.push({type: 'cost_item', text: cleanText, name: 'Cost Item'});
    } else if (costItemDescHighlights?.length) {
      const cleanText = costItemDescHighlights[0].replace(/<\/?em>/g, '');
      highlights.push({type: 'cost_item', text: cleanText.substring(0, 50) + '...', name: 'Cost Item'});
    }
    
    return highlights.slice(0, 2); // Max 2 highlights to avoid clutter
  };

  return (
    <div 
      className={`compact-budget-card ${selected ? 'selected' : ''} ${darkMode ? 'dark' : ''}`}
      onClick={onClick}>
      {/* Selection checkbox */}
      {onSelect && (
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(result.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="card-select"
        />
      )}

      {/* Organization Badge */}
      <div className="org-badge">
        {result.organization}
      </div>

      {/* Title */}
      <h3 className="card-title">
        {renderHighlightedText(result.line_item_title, 'line_item_title', 60)}
      </h3>

      {/* Meta Info */}
      <div className="card-meta">
        <span className="meta-item">{result.account_title || 'N/A'}</span>
      </div>

      {/* Tags */}
      <div className="card-tags">
        {result.domain && (
          <span className="tag tag-domain">{result.domain}</span>
        )}
        {result.program && (
          <span className="tag tag-program">{result.program}</span>
        )}
      </div>

      {/* Description */}
      {result.line_item_description && (
        <p className="card-description">
          {renderHighlightedText(result.line_item_description, 'line_item_description', 120)}
        </p>
      )}

      {/* Nested Highlights Preview */}
      {(() => {
        const nestedHighlights = getNestedHighlights();
        return nestedHighlights.length > 0 && (
          <div className="nested-highlights">
            {nestedHighlights.map((highlight, idx) => (
              <div key={idx} className={`highlight-preview highlight-${highlight.type}`}>
                <span className="highlight-label">{highlight.name}:</span>
                <span className="highlight-text">{highlight.text}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Stats Bar */}
      <div className="card-stats">
        <div className="stat">
          <span className="stat-label">Projects</span>
          <span className="stat-value">{getProjectCount()}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Cost Items</span>
          <span className="stat-value">{getCostItemCount()}</span>
        </div>
      </div>

      {/* Footer with Budget */}
      <div className="card-footer">
        <div className="budget-amount">
          {formatCurrency(result.total_budget)}
          <span className="budget-label">/total</span>
        </div>
        {result.relevance_score && (
          <div className="relevance-score" title={`Relevance: ${(result.relevance_score * 100).toFixed(0)}%`}>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ width: `${Math.min(result.relevance_score * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Bookmark Button - positioned absolutely in bottom right */}
      {onBookmark && result.peid && (
        <button
          className={`bookmark-btn-overlay ${isBookmarked ? 'bookmarked' : ''}`}
          onClick={handleBookmarkClick}
          title={isBookmarked ? 'Remove bookmark' : 'Bookmark this item'}
        >
          {isBookmarked ? '★' : '☆'}
        </button>
      )}
    </div>
  );
};

export default CompactBudgetCard;